import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import type { AdminUser } from '../../../hooks/useAdminAuth';
import { useAdminAuth } from '../../../hooks/useAdminAuth';
import { useToast } from '../../../hooks/useToast';

export default function AdminManageAdminsTab() {
  const { adminRecord, isSuperAdmin } = useAdminAuth();
  const { addToast } = useToast();

  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [loadingList, setLoadingList] = useState(true);

  const [inviteEmail, setInviteEmail] = useState('');
  const [invitePassword, setInvitePassword] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'super_admin'>('admin');
  const [inviting, setInviting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Edit role state
  const [editingRole, setEditingRole] = useState<string | null>(null);
  const [savingRole, setSavingRole] = useState(false);
  const [selectedRole, setSelectedRole] = useState<'admin' | 'super_admin'>('admin');

  const loadAdmins = async () => {
    if (!adminRecord) return;
    setLoadingList(true);
    // Use security-definer RPC to bypass RLS
    const { data } = await supabase.rpc('get_all_admins', { requesting_user_id: adminRecord.user_id });
    setAdmins((data as AdminUser[]) ?? []);
    setLoadingList(false);
  };

  useEffect(() => {
    if (adminRecord) loadAdmins();
  }, [adminRecord]);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim() || !invitePassword.trim()) return;
    setInviting(true);

    const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
      email: inviteEmail.trim(),
      password: invitePassword,
    });

    if (signUpError || !signUpData?.user) {
      // Fallback: insert without user_id — user links on first login
      const { error: insertError } = await supabase.from('admin_users').insert({
        email: inviteEmail.trim(),
        role: inviteRole,
        invited_by: adminRecord?.email ?? 'system',
      });
      if (insertError) {
        addToast({ type: 'error', title: 'Error', message: insertError.message });
      } else {
        addToast({ type: 'success', title: 'Admin Added', message: `${inviteEmail} added — they must sign up with this email to activate.` });
        setInviteEmail('');
        setInvitePassword('');
        loadAdmins();
      }
    } else {
      const { error: insertError } = await supabase.from('admin_users').insert({
        user_id: signUpData.user.id,
        email: inviteEmail.trim(),
        role: inviteRole,
        invited_by: adminRecord?.email ?? 'system',
      });
      if (insertError) {
        addToast({ type: 'error', title: 'Error', message: insertError.message });
      } else {
        addToast({ type: 'success', title: 'Admin Created', message: `${inviteEmail} has been added as ${inviteRole}.` });
        setInviteEmail('');
        setInvitePassword('');
        loadAdmins();
      }
    }
    setInviting(false);
  };

  const handleDelete = async (admin: AdminUser) => {
    setDeleting(true);
    const { error } = await supabase.from('admin_users').delete().eq('id', admin.id);
    if (error) {
      addToast({ type: 'error', title: 'Error', message: error.message });
    } else {
      addToast({ type: 'success', title: 'Admin Removed', message: `${admin.email} has been removed.` });
      loadAdmins();
    }
    setDeleteConfirm(null);
    setDeleting(false);
  };

  const handleEditRole = (admin: AdminUser) => {
    setEditingRole(admin.id);
    setSelectedRole(admin.role);
  };

  const handleSaveRole = async (admin: AdminUser) => {
    setSavingRole(true);
    const { error } = await supabase
      .from('admin_users')
      .update({ role: selectedRole })
      .eq('id', admin.id);
    if (error) {
      addToast({ type: 'error', title: 'Error', message: error.message });
    } else {
      addToast({ type: 'success', title: 'Role Updated', message: `${admin.email}'s role changed to ${selectedRole}.` });
      loadAdmins();
    }
    setEditingRole(null);
    setSavingRole(false);
  };

  const inputCls = 'w-full bg-[#1a3333] border border-teal-900/40 rounded-xl px-4 py-3 text-sm text-white outline-none placeholder-teal-700 focus:border-teal-600 transition-colors';

  return (
    <div className="space-y-8">
      {/* Current Admins */}
      <div className="bg-[#0d2020] border border-white/5 rounded-2xl overflow-hidden">
        <div className="px-6 py-5 border-b border-white/5 flex items-center justify-between">
          <div>
            <h3 className="font-heading font-semibold text-white text-base">Admin Accounts</h3>
            <p className="text-teal-600 text-xs mt-0.5">All users with admin access — editable and removable</p>
          </div>
          {loadingList && (
            <div className="w-5 h-5 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
          )}
        </div>

        {!loadingList && admins.length === 0 ? (
          <div className="text-center py-12 text-teal-700">
            <i className="ri-user-settings-line text-3xl mb-3 block" />
            <p className="text-sm">No admin accounts yet.</p>
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {admins.map((admin) => {
              const isSelf = admin.id === adminRecord?.id;
              return (
                <div key={admin.id} className="flex items-center gap-4 px-6 py-4">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm uppercase"
                    style={{ background: 'var(--primary)', color: 'white' }}
                  >
                    {admin.email.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-white text-sm font-medium truncate">{admin.email}</span>
                      {isSelf && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-teal-900/40 text-teal-400 border border-teal-700/30">You</span>
                      )}
                    </div>

                    {/* Role display / edit */}
                    {editingRole === admin.id ? (
                      <div className="flex items-center gap-2 mt-1.5">
                        <select
                          value={selectedRole}
                          onChange={(e) => setSelectedRole(e.target.value as 'admin' | 'super_admin')}
                          className="bg-[#1a3333] border border-teal-700/40 rounded-lg px-2 py-1 text-xs text-white outline-none"
                        >
                          <option value="admin">Admin</option>
                          <option value="super_admin">Super Admin</option>
                        </select>
                        <button
                          onClick={() => handleSaveRole(admin)}
                          disabled={savingRole}
                          className="px-2 py-1 rounded-lg text-xs font-semibold bg-teal-500/20 text-teal-400 border border-teal-500/30 hover:bg-teal-500/30 cursor-pointer transition-colors"
                        >
                          {savingRole ? '...' : 'Save'}
                        </button>
                        <button
                          onClick={() => setEditingRole(null)}
                          className="px-2 py-1 rounded-lg text-xs bg-white/5 text-teal-500 border border-white/10 hover:bg-white/10 cursor-pointer transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 mt-0.5">
                        <span className={`text-xs font-semibold ${admin.role === 'super_admin' ? 'text-amber-400' : 'text-teal-500'}`}>
                          <i className={`${admin.role === 'super_admin' ? 'ri-star-line' : 'ri-user-line'} mr-1`} />
                          {admin.role === 'super_admin' ? 'Super Admin' : 'Admin'}
                        </span>
                        <span className="text-teal-800 text-xs">
                          Added {new Date(admin.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Action buttons — super admins can edit/delete any account including their own */}
                  {isSuperAdmin && editingRole !== admin.id && (
                    <div className="flex items-center gap-1">
                      {/* Edit role button */}
                      <button
                        onClick={() => handleEditRole(admin)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-teal-600 hover:text-teal-300 hover:bg-teal-900/30 transition-colors cursor-pointer"
                        title="Edit role"
                      >
                        <i className="ri-edit-line text-sm" />
                      </button>

                      {/* Delete button */}
                      {deleteConfirm === admin.id ? (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-red-400">Remove?</span>
                          <button
                            onClick={() => handleDelete(admin)}
                            disabled={deleting}
                            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30 transition-colors cursor-pointer"
                          >
                            {deleting ? '...' : 'Yes'}
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(null)}
                            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-teal-900/30 text-teal-400 border border-teal-800/40 hover:bg-teal-800/30 transition-colors cursor-pointer"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setDeleteConfirm(admin.id)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg text-teal-700 hover:text-red-400 hover:bg-red-900/20 transition-colors cursor-pointer"
                          title="Remove admin"
                        >
                          <i className="ri-delete-bin-line text-sm" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add Admin */}
      {isSuperAdmin ? (
        <div className="bg-[#0d2020] border border-white/5 rounded-2xl p-6">
          <h3 className="font-heading font-semibold text-white text-base mb-1">Add New Admin</h3>
          <p className="text-teal-600 text-xs mb-6">Grant admin access to a new team member. Their account is created immediately.</p>

          <form onSubmit={handleInvite} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-teal-500 font-semibold uppercase tracking-wide block mb-2">Email Address</label>
                <div className="relative">
                  <i className="ri-mail-line absolute left-4 top-1/2 -translate-y-1/2 text-teal-600 text-sm pointer-events-none" />
                  <input
                    type="email"
                    required
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="newadmin@company.com"
                    className={`${inputCls} pl-10`}
                  />
                </div>
              </div>
              <div>
                <label className="text-xs text-teal-500 font-semibold uppercase tracking-wide block mb-2">
                  Password
                  <span className="ml-1 text-teal-700 normal-case font-normal">(changeable later)</span>
                </label>
                <div className="relative">
                  <i className="ri-lock-line absolute left-4 top-1/2 -translate-y-1/2 text-teal-600 text-sm pointer-events-none" />
                  <input
                    type="password"
                    required
                    value={invitePassword}
                    onChange={(e) => setInvitePassword(e.target.value)}
                    placeholder="Minimum 6 characters"
                    minLength={6}
                    className={`${inputCls} pl-10`}
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="text-xs text-teal-500 font-semibold uppercase tracking-wide block mb-3">Role</label>
              <div className="flex gap-3">
                {(['admin', 'super_admin'] as const).map((role) => (
                  <button
                    key={role}
                    type="button"
                    onClick={() => setInviteRole(role)}
                    className={`flex items-center gap-2 px-4 py-3 rounded-xl border text-sm font-medium transition-all cursor-pointer ${
                      inviteRole === role
                        ? 'border-teal-500 bg-teal-900/40 text-teal-300'
                        : 'border-teal-900/40 bg-[#1a3333] text-teal-600 hover:border-teal-700 hover:text-teal-400'
                    }`}
                  >
                    <i className={role === 'super_admin' ? 'ri-star-line text-amber-400' : 'ri-user-line'} />
                    {role === 'super_admin' ? 'Super Admin' : 'Admin'}
                    {inviteRole === role && <i className="ri-check-line text-xs ml-1" />}
                  </button>
                ))}
              </div>
              <p className="text-teal-700 text-xs mt-2">
                {inviteRole === 'super_admin'
                  ? 'Super Admins can add/remove/edit all admin accounts and have full access.'
                  : 'Admins can create and manage shipments but cannot manage admin accounts.'}
              </p>
            </div>

            <button
              type="submit"
              disabled={inviting}
              className="flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60 cursor-pointer"
              style={{ background: 'var(--primary)' }}
            >
              {inviting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Creating Account...
                </>
              ) : (
                <>
                  <i className="ri-user-add-line" />
                  Add Admin
                </>
              )}
            </button>
          </form>
        </div>
      ) : (
        <div className="bg-[#0d2020] border border-white/5 rounded-2xl p-6 text-center">
          <div className="w-12 h-12 rounded-full bg-teal-900/30 flex items-center justify-center mx-auto mb-3">
            <i className="ri-shield-keyhole-line text-xl text-teal-600" />
          </div>
          <p className="text-teal-500 text-sm">Only Super Admins can add or remove admin accounts.</p>
          <p className="text-teal-700 text-xs mt-1">Contact your Super Admin to update team access.</p>
        </div>
      )}
    </div>
  );
}
